
import React from 'react'
import { Button } from 'primereact/button'
import { TabView, TabPanel } from 'primereact/tabview'
import { Tag } from 'primereact/tag'
import { DataView } from 'primereact/dataview'

import { Movie, CinemaNews } from '../api/movies'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet-async'

import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../store/store'
import { setLanguage } from '../store/slices/languageSlice'
import { toggleMode } from '../store/slices/themeSlice'
import ClassicAppBar from '../components/ClassicAppBar'
import MovieCard from '../components/cinema/MovieCard'
import NewsCard from '../components/cinema/NewsCard'
import CinemaFilters from '../components/cinema/CinemaFilters'
import { useCinemaData } from '../hooks/useCinemaData'

const CinemaPage: React.FC = () => {
  const { i18n } = useTranslation()
  const dispatch = useDispatch()
  const themeMode = useSelector((state: RootState) => state.theme?.mode || 'light')

  const { movies, news, loading } = useCinemaData()

  const [first, setFirst] = React.useState(0)
  const [searchQuery, setSearchQuery] = React.useState('')
  const [selectedGenre, setSelectedGenre] = React.useState<string | null>(null)
  const [selectedYear, setSelectedYear] = React.useState<string | null>(null)
  const [selectedRating, setSelectedRating] = React.useState<string | null>(null)

  const handleLanguageChange = (lang: 'en' | 'ta' | 'de') => {
    i18n.changeLanguage(lang)
    dispatch(setLanguage(lang))
  }

  const handleToggleMode = () => {
    dispatch(toggleMode())
  }

  const filteredMovies = React.useMemo(() => {
    let filtered = movies

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(movie =>
        movie.title.toLowerCase().includes(query) ||
        (movie.titleTamil && movie.titleTamil.includes(searchQuery)) ||
        (movie.director && movie.director.toLowerCase().includes(query)) ||
        (movie.cast && movie.cast.toLowerCase().includes(query))
      )
    }

    if (selectedGenre) {
      filtered = filtered.filter(movie => movie.genre === selectedGenre)
    }

    if (selectedYear) {
      filtered = filtered.filter(movie => movie.year && movie.year.toString() === selectedYear)
    }

    if (selectedRating) {
      const minRating = parseFloat(selectedRating)
      filtered = filtered.filter(movie => movie.rating && movie.rating >= minRating)
    }

    return filtered
  }, [movies, searchQuery, selectedGenre, selectedYear, selectedRating])

  const movieTemplate = (movie: Movie) => <MovieCard movie={movie} />
  const newsTemplate = (item: CinemaNews) => <NewsCard item={item} />

  return (
    <div className="min-h-screen flex flex-column cinema-page" style={{ background: '#020617' }}> {/* Force dark background */}
      <Helmet>
        <title>Cinema · YarlHub</title>
        <meta name="description" content="Tamil cinema news and movie directory" />
        <link rel="canonical" href={typeof window !== 'undefined' ? window.location.href : undefined} />
      </Helmet>

      <ClassicAppBar
        mode={themeMode}
        onToggleMode={handleToggleMode}
        onChangeLang={handleLanguageChange}
      />

      {/* Hero Section */}
      <div className="bg-gray-900 text-white relative overflow-hidden shadow-8" style={{ minHeight: '400px' }}>
        <div className="absolute top-0 left-0 w-full h-full">
          <img
            src="https://images.unsplash.com/photo-1536440136628-849c177e76a1?q=80&w=2525&auto=format&fit=crop"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            alt="Cinema Hero"
          />
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-gray-950 via-gray-900 to-black-alpha-80 opacity-95"></div>
        </div>

        <div className="container mx-auto p-4 lg:p-6 max-w-7xl relative z-1 flex flex-column justify-content-center h-full" style={{ minHeight: '400px' }}>
          <div className="flex align-items-center gap-3 mb-4 animate-fade-in-down">
            <Tag value="YarlHub Cinema" className="bg-orange-500 text-white font-bold text-base px-3 py-2"></Tag>
            <span className="text-gray-300 font-medium"><i className="pi pi-bolt text-yellow-500 mr-2"></i>Trending Now</span>
          </div>
          <h1 className="text-6xl md:text-8xl font-bold m-0 mb-4 animate-fade-in-left text-white text-shadow-1">
            Tamil <span className="text-orange-500">Cinema</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 max-w-3xl line-height-3 animate-fade-in-up shadow-1">
            Discover the latest blockbusters, timeless classics, and breaking news from the world of Kollywood.
          </p>

          <div className="flex flex-wrap gap-3 mt-5 animate-fade-in-up">
            <Button
              label="New Releases"
              icon="pi pi-play"
              className="p-button-rounded border-none px-5 py-3 font-bold text-lg hover:scale-105 transition-all transition-duration-200 shadow-4 flex align-items-center gap-2"
              style={{
                background: 'linear-gradient(90deg, #ea580c 0%, #f97316 100%)',
                boxShadow: '0 4px 15px rgba(234, 88, 12, 0.4)'
              }}
            />
            <Button
              label="Browse Genre"
              icon="pi pi-list"
              className="p-button-rounded p-button-outlined text-white border-white px-5 py-3 font-bold text-lg hover:bg-white-alpha-10 backdrop-blur-sm transition-all transition-duration-200 flex align-items-center gap-2"
            />
          </div>
        </div>
      </div>

      <div className="w-full relative z-2 mt-4 md:mt-6">
        <TabView className="p-tabview-custom">
          <TabPanel header="Movies Library" leftIcon="pi pi-video">
            <div className="px-4 lg:px-6 mb-4">
              <CinemaFilters
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                selectedGenre={selectedGenre}
                setSelectedGenre={setSelectedGenre}
                selectedYear={selectedYear}
                setSelectedYear={setSelectedYear}
                selectedRating={selectedRating}
                setSelectedRating={setSelectedRating}
                setFirst={setFirst}
              />
            </div>

            {loading ? (
              <div className="flex justify-content-center p-8">
                <i className="pi pi-spin pi-spinner text-orange-500 text-4xl"></i>
              </div>
            ) : (
              <div className="px-4 lg:px-6">
                <DataView
                  value={filteredMovies}
                  itemTemplate={movieTemplate}
                  layout="grid"
                  paginator
                  rows={12}
                  first={first}
                  onPage={(e) => setFirst(e.first)}
                  emptyMessage="No movies found."
                  className="w-full"
                  paginatorClassName="bg-transparent border-none mt-4"
                />
              </div>
            )}
          </TabPanel>

          <TabPanel header="Latest News" leftIcon="pi pi-megaphone">
            {loading ? (
              <div className="flex justify-content-center p-8">
                <i className="pi pi-spin pi-spinner text-orange-500 text-4xl"></i>
              </div>
            ) : (
              <div className="px-4 lg:px-6">
                <DataView
                  value={news}
                  itemTemplate={newsTemplate}
                  layout="grid"
                  paginator
                  rows={6}
                  emptyMessage="No news found."
                  className="w-full"
                  paginatorClassName="bg-transparent border-none mt-4"
                />
              </div>
            )}
          </TabPanel>
        </TabView>
      </div>
    </div>
  )
}

export default CinemaPage
